import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MessageCircle, Send, LogOut, Users, Calendar, Trash2, Instagram, Plus, X, Zap } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Tavolo {
  id: string;
  table_number: number;
  capacity: number;
  status: string;
}

interface Message {
  id: string;
  sender_table_id: string;
  receiver_table_id: string;
  message: string;
  is_challenge: boolean;
  created_at: string;
  sender_table_number?: number;
  receiver_table_number?: number;
}

interface SocialProfile {
  id: string;
  tavolo_id: string;
  name: string;
  instagram_url: string;
}

const MessaggeriaPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const locationId = searchParams.get('loc') || '';
  const tavoloId = searchParams.get('tid') || '';
  const locationName = searchParams.get('lname') || '';
  const tableNumber = parseInt(searchParams.get('tn') || '0', 10);

  const [tavoli, setTavoli] = useState<Tavolo[]>([]);
  const [sentMessages, setSentMessages] = useState<Message[]>([]);
  const [receivedMessages, setReceivedMessages] = useState<Message[]>([]);
  const [socialProfiles, setSocialProfiles] = useState<Record<string, SocialProfile[]>>({});

  const [recipientTableId, setRecipientTableId] = useState('');
  const [messageText, setMessageText] = useState('');
  const [isChallenge, setIsChallenge] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const [showSocialModal, setShowSocialModal] = useState(false);
  const [socialName, setSocialName] = useState('');
  const [socialInstagram, setSocialInstagram] = useState('');
  const [addingSocial, setAddingSocial] = useState(false);

  const [selectedTavoloForSocial, setSelectedTavoloForSocial] = useState<string | null>(null);

  const loadTavoli = useCallback(async () => {
    if (!locationId) return;
    const { data } = await supabase
      .from('tavoli')
      .select('id, table_number, capacity, status')
      .eq('location_id', locationId)
      .order('table_number');
    setTavoli(data || []);
  }, [locationId]);

  const loadMessages = useCallback(async () => {
    if (!tavoloId || !locationId) return;

    const { data: allTavoli } = await supabase
      .from('tavoli')
      .select('id, table_number')
      .eq('location_id', locationId);

    const tavoloMap = new Map((allTavoli || []).map(t => [t.id, t.table_number]));

    const { data: sent } = await supabase
      .from('table_messages')
      .select('*')
      .eq('sender_table_id', tavoloId)
      .eq('location_id', locationId)
      .order('created_at', { ascending: false });

    const { data: received } = await supabase
      .from('table_messages')
      .select('*')
      .eq('receiver_table_id', tavoloId)
      .eq('location_id', locationId)
      .order('created_at', { ascending: false });

    const mapMsg = (m: any): Message => ({
      ...m,
      sender_table_number: tavoloMap.get(m.sender_table_id),
      receiver_table_number: tavoloMap.get(m.receiver_table_id),
    });

    setSentMessages((sent || []).map(mapMsg));
    setReceivedMessages((received || []).map(mapMsg));
  }, [tavoloId, locationId]);

  const loadSocialProfiles = useCallback(async () => {
    if (!locationId) return;
    const { data: allTavoli } = await supabase
      .from('tavoli')
      .select('id')
      .eq('location_id', locationId);

    if (!allTavoli || allTavoli.length === 0) return;

    const ids = allTavoli.map(t => t.id);
    const { data } = await supabase
      .from('table_social_profiles')
      .select('*')
      .in('tavolo_id', ids);

    const grouped: Record<string, SocialProfile[]> = {};
    (data || []).forEach(p => {
      if (!grouped[p.tavolo_id]) grouped[p.tavolo_id] = [];
      grouped[p.tavolo_id].push(p);
    });
    setSocialProfiles(grouped);
  }, [locationId]);

  useEffect(() => {
    if (!locationId || !tavoloId) {
      navigate('/');
      return;
    }
    loadTavoli();
    loadMessages();
    loadSocialProfiles();
  }, [locationId, tavoloId, navigate, loadTavoli, loadMessages, loadSocialProfiles]);


  const handleSendMessage = async () => {
    if (!recipientTableId || !messageText.trim()) return;
    if (recipientTableId === tavoloId) {
      setError('Non puoi inviare un messaggio al tuo stesso tavolo.');
      return;
    }

    setSending(true);
    setError('');

    try {
      const { error } = await supabase
        .from('table_messages')
        .insert({
          location_id: locationId,
          sender_table_id: tavoloId,
          receiver_table_id: recipientTableId,
          message: messageText.trim(),
          is_challenge: isChallenge,
        });

      if (error) throw error;
      setMessageText('');
      setRecipientTableId('');
      setIsChallenge(false);
      await loadMessages();
    } catch (err: any) {
      setError(err.message || 'Errore nell\'invio del messaggio.');
    } finally {
      setSending(false);
    }
  };

  const handleAddSocialProfile = async () => {
    const targetTavolo = selectedTavoloForSocial || tavoloId;
    if (!socialName.trim() && !socialInstagram.trim()) return;
    setAddingSocial(true);

    try {
      const { error } = await supabase
        .from('table_social_profiles')
        .insert({
          tavolo_id: targetTavolo,
          name: socialName.trim(),
          instagram_url: socialInstagram.trim(),
        });

      if (error) throw error;
      setSocialName('');
      setSocialInstagram('');
      setShowSocialModal(false);
      setSelectedTavoloForSocial(null);
      await loadSocialProfiles();
    } catch (err: any) {
      console.error('Error adding social profile:', err);
    } finally {
      setAddingSocial(false);
    }
  };

  const handleDeleteSocialProfile = async (profileId: string) => {
    try {
      const { error } = await supabase
        .from('table_social_profiles')
        .delete()
        .eq('id', profileId);
      if (error) throw error;
      await loadSocialProfiles();
    } catch (err: any) {
      console.error('Error deleting social profile:', err);
    }
  };

  const handleExit = () => {
    navigate('/');
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const otherTavoli = tavoli.filter(t => t.id !== tavoloId);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2.5">
              <MessageCircle className="h-5 w-5" />
              <span className="text-lg font-bold">Messaggi {locationName}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-blue-100">Tavolo {tableNumber}</span>
              <button
                onClick={() => {
                  setSelectedTavoloForSocial(tavoloId);
                  setShowSocialModal(true);
                }}
                className="p-1.5 hover:bg-white/15 rounded-lg transition-colors"
                title="Aggiungi profilo social"
              >
                <Users className="h-5 w-5" />
              </button>
              <button
                onClick={handleExit}
                className="flex items-center gap-1.5 text-sm font-medium hover:bg-white/15 px-3 py-1.5 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Esci
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Top 3-column section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* New Message */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <MessageCircle className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-bold text-gray-900">Nuovo Messaggio</h2>
            </div>

            {error && (
              <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-3">
              <select
                value={recipientTableId}
                onChange={(e) => setRecipientTableId(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Seleziona Tavolo Destinatario</option>
                {otherTavoli.map(t => (
                  <option key={t.id} value={t.id}>Tavolo {t.table_number}</option>
                ))}
              </select>

              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Messaggio"
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 resize-y focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
              />

              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={isChallenge}
                  onChange={(e) => setIsChallenge(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Zap className="h-4 w-4 text-amber-500" />
                Invia come Sfida
              </label>

              <button
                onClick={handleSendMessage}
                disabled={sending || !recipientTableId || !messageText.trim()}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2.5 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <MessageCircle className="h-4 w-4" />
                Invia Messaggio
              </button>
            </div>
          </div>

          {/* Sent Messages */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Messaggi Inviati</h2>
            <div className="flex-1 overflow-y-auto max-h-[400px] space-y-3">
              {sentMessages.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-12">Nessun messaggio inviato</p>
              ) : (
                sentMessages.map(msg => (
                  <div
                    key={msg.id}
                    className={`p-3 rounded-lg border ${
                      msg.is_challenge
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-blue-50 border-blue-100'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-500">
                        A Tavolo {msg.receiver_table_number}
                      </span>
                      <span className="text-xs text-gray-400">{formatTime(msg.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-800">{msg.message}</p>
                    {msg.is_challenge && (
                      <span className="inline-flex items-center gap-1 mt-1.5 text-xs font-medium text-amber-600">
                        <Zap className="h-3 w-3" /> Sfida
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Received Messages */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Messaggi Ricevuti</h2>
            <div className="flex-1 overflow-y-auto max-h-[400px] space-y-3">
              {receivedMessages.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-12">Nessun messaggio ricevuto</p>
              ) : (
                receivedMessages.map(msg => (
                  <div
                    key={msg.id}
                    className={`p-3 rounded-lg border ${
                      msg.is_challenge
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-green-50 border-green-100'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-500">
                        Da Tavolo {msg.sender_table_number}
                      </span>
                      <span className="text-xs text-gray-400">{formatTime(msg.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-800">{msg.message}</p>
                    {msg.is_challenge && (
                      <span className="inline-flex items-center gap-1 mt-1.5 text-xs font-medium text-amber-600">
                        <Zap className="h-3 w-3" /> Sfida
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Tavoli e Profili Social */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-5">
            <Users className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-bold text-gray-900">Tavoli e Profili Social</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {tavoli.map(t => {
              const isCurrent = t.id === tavoloId;
              const profiles = socialProfiles[t.id] || [];

              return (
                <div
                  key={t.id}
                  className={`rounded-xl border-2 p-4 transition-all ${
                    isCurrent
                      ? 'border-blue-500 bg-blue-50/40'
                      : 'border-gray-200 bg-green-50/30 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold text-gray-900">
                      Tavolo {t.table_number}
                      {isCurrent && <span className="text-xs text-blue-600 ml-1.5">(Tu)</span>}
                    </h3>
                    {!isCurrent && (
                      <button
                        onClick={() => {
                          setRecipientTableId(t.id);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="p-1 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Invia messaggio"
                      >
                        <Send className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-emerald-600 font-medium mb-3">Disponibile</p>

                  {/* Social profiles for this table */}
                  {profiles.length > 0 && (
                    <div className="space-y-1.5 mb-2">
                      {profiles.map(p => (
                        <div key={p.id} className="flex items-center gap-2 text-sm">
                          <Instagram className="h-3.5 w-3.5 text-pink-500 flex-shrink-0" />
                          {p.instagram_url ? (
                            <a
                              href={p.instagram_url.startsWith('http') ? p.instagram_url : `https://instagram.com/${p.instagram_url.replace('@', '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline truncate"
                            >
                              {p.name || p.instagram_url}
                            </a>
                          ) : (
                            <span className="text-gray-600 truncate">{p.name}</span>
                          )}
                          {isCurrent && (
                            <button
                              onClick={() => handleDeleteSocialProfile(p.id)}
                              className="ml-auto p-0.5 text-red-400 hover:text-red-600 flex-shrink-0"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {isCurrent && (
                    <button
                      onClick={() => {
                        setSelectedTavoloForSocial(t.id);
                        setShowSocialModal(true);
                      }}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-1"
                    >
                      <Plus className="h-3 w-3" />
                      Aggiungi profilo
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Social Profile Modal */}
      {showSocialModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowSocialModal(false); setSelectedTavoloForSocial(null); }}>
          <div
            className="bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-gray-900">Aggiungi Profilo Social</h3>
                <button
                  onClick={() => { setShowSocialModal(false); setSelectedTavoloForSocial(null); }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <input
                    type="text"
                    value={socialName}
                    onChange={(e) => setSocialName(e.target.value)}
                    placeholder="Il tuo nome"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                  <input
                    type="text"
                    value={socialInstagram}
                    onChange={(e) => setSocialInstagram(e.target.value)}
                    placeholder="@username o URL profilo"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700"
                  />
                </div>
                <button
                  onClick={handleAddSocialProfile}
                  disabled={addingSocial || (!socialName.trim() && !socialInstagram.trim())}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2.5 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingSocial ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessaggeriaPage;
